/* Scandinavian Edition — measurement lines (A–D) drawn over the product's front packshot.
   1. A model listed in snippets/se-measure-lines.liquid uses those positions.
   2. Otherwise the garment is found in a small copy of the photo (the studio background is flat):
      shoulders, hem, body width and the right sleeve give the four lines.
   3. If neither works, the built-in drawing is shown instead.
   Adjust mode: add ?se-measure=edit to the URL (?se-measure=off to leave). Drag the white dots, then
   "Copy all" and paste the result into snippets/se-measure-lines.liquid. Edits are kept in this browser
   until then and are only shown in adjust mode. */
(() => {
  const qs = location.search;
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
    del(k) { try { localStorage.removeItem(k); } catch (e) {} },
  };
  if (/[?&]se-measure=edit\b/.test(qs)) store.set('se-measure-edit', '1');
  if (/[?&]se-measure=off\b/.test(qs)) store.del('se-measure-edit');
  /* The bulk editor page (collection.se-measure-editor) is always in adjust mode */
  const BULK = !!document.querySelector('[data-se-measure-bulk]');
  const EDIT = BULK || store.get('se-measure-edit') === '1';
  const LOCAL_KEY = 'se-measure-local';

  const readJSON = (s, fb) => { try { return JSON.parse(s) || fb; } catch (e) { return fb; } };
  const themeLines = readJSON((document.querySelector('[data-se-measure-lines]') || {}).textContent, {});
  const localLines = () => readJSON(store.get(LOCAL_KEY), {});

  /* Starting point when nothing else is known: the built-in drawing, in fractions of the photo */
  const TEMPLATE = {
    A: [0.32, 0.68, 0.40],
    B: [0.33, 0.67, 0.78],
    C: [0.58, 0.20, 0.74, 0.25, 0.86, 0.80],
    D: [0.54, 0.22, 0.90],
  };

  const clone = (o) => JSON.parse(JSON.stringify(o));
  const r3 = (n) => Math.round(n * 1000) / 1000;
  const tidy = (L) => {
    const o = {};
    ['A', 'B', 'C', 'D'].forEach((k) => { o[k] = L[k] ? L[k].map(r3) : null; });
    return o;
  };

  /* ---------- Finding the garment ---------- */
  const detect = (img, kind) => {
    const W = 160;
    const H = Math.round((img.naturalHeight / img.naturalWidth) * W);
    if (!H) return null;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    const px = ctx.getImageData(0, 0, W, H).data;

    /* Backdrop colour: the four corners */
    let br = 0, bgc = 0, bb = 0, n = 0;
    [[0, 0], [W - 4, 0], [0, H - 4], [W - 4, H - 4]].forEach(([x0, y0]) => {
      for (let y = y0; y < y0 + 4; y++) for (let x = x0; x < x0 + 4; x++) {
        const i = (y * W + x) * 4;
        br += px[i]; bgc += px[i + 1]; bb += px[i + 2]; n++;
      }
    });
    br /= n; bgc /= n; bb /= n;
    const near = (i) => Math.abs(px[i] - br) + Math.abs(px[i + 1] - bgc) + Math.abs(px[i + 2] - bb) < 26;

    /* Backdrop = everything backdrop-coloured that is reachable from the edge. The rest is garment. */
    const bg = new Uint8Array(W * H);
    const stack = [];
    const push = (x, y) => {
      if (x < 0 || y < 0 || x >= W || y >= H) return;
      const p = y * W + x;
      if (bg[p] || !near(p * 4)) return;
      bg[p] = 1;
      stack.push(p);
    };
    for (let x = 0; x < W; x++) { push(x, 0); push(x, H - 1); }
    for (let y = 0; y < H; y++) { push(0, y); push(W - 1, y); }
    while (stack.length) {
      const p = stack.pop();
      const x = p % W, y = (p / W) | 0;
      push(x + 1, y); push(x - 1, y); push(x, y + 1); push(x, y - 1);
    }
    const on = (x, y) => x >= 0 && y >= 0 && x < W && y < H && !bg[y * W + x];

    /* Row extents */
    const L = new Array(H).fill(-1), R = new Array(H).fill(-1);
    let top = -1, bottom = -1, maxW = 0, minL = W, maxR = 0;
    for (let y = 0; y < H; y++) {
      let cnt = 0;
      for (let x = 0; x < W; x++) if (on(x, y)) { cnt++; if (L[y] < 0) L[y] = x; R[y] = x; }
      if (cnt < 2) { L[y] = R[y] = -1; continue; }
      if (top < 0) top = y;
      bottom = y;
      maxW = Math.max(maxW, R[y] - L[y]);
      minL = Math.min(minL, L[y]);
      maxR = Math.max(maxR, R[y]);
    }
    if (top < 0 || maxW < W * 0.25 || bottom - top < H * 0.3) return null;
    const xc = Math.round((minL + maxR) / 2);
    const width = (y) => (L[y] < 0 ? 0 : R[y] - L[y]);

    /* Shoulders: the first row noticeably wider than the collar or hood */
    let ys = -1;
    for (let y = top; y < bottom; y++) if (width(y) >= maxW * 0.55) { ys = y; break; }
    if (ys < 0) return null;

    /* Hem: the lowest garment pixel in the centre column */
    let yHem = -1;
    for (let y = bottom; y > ys; y--) {
      if (on(xc, y) || on(xc - 1, y) || on(xc + 1, y)) { yHem = y; break; }
    }
    const len = yHem - ys;
    if (len < H * 0.25) return null;

    /* The garment segment around the centre in a row (body only, when the sleeves hang free) */
    const seg = (y) => {
      let l = xc, r = xc;
      if (!on(xc, y)) return null;
      while (on(l - 1, y)) l--;
      while (on(r + 1, y)) r++;
      return [l, r];
    };
    const yShoulder = Math.min(bottom, ys + Math.round(len * 0.04));
    const shoulderW = width(yShoulder);
    const segW = (y) => { const s = seg(y); return s ? s[1] - s[0] : 0; };

    /* Body width: the centre segment low on the body, where the sleeves usually hang free */
    const lows = [];
    for (let y = Math.round(ys + len * 0.6); y <= ys + len * 0.9; y++) { const w = segW(y); if (w) lows.push(w); }
    if (!lows.length) return null;
    lows.sort((p, q) => p - q);
    let bodyW = lows[lows.length >> 1];
    /* Sleeves touching the body all the way down: fall back to a share of the shoulder width */
    const sleevesFree = bodyW < width(Math.round(ys + len * 0.75)) * 0.85;
    if (!sleevesFree && kind !== 'vest') bodyW = Math.min(bodyW, shoulderW * 0.82);

    /* Armpit: going up from the waist, where the centre segment joins the sleeves. A sits just below. */
    let yA = Math.round(ys + len * 0.27);
    if (sleevesFree && kind !== 'vest') {
      for (let y = Math.round(ys + len * 0.6); y > ys; y--) {
        if (segW(y) > bodyW * 1.2) { yA = Math.round(y + len * 0.04); break; }
      }
    }
    const fit = (y) => {
      const s = seg(y);
      if (s && s[1] - s[0] <= bodyW * 1.15 && s[1] - s[0] >= bodyW * 0.75) return s;
      return [xc - bodyW / 2, xc + bodyW / 2];
    };
    const a = fit(yA);
    const aw = a[1] - a[0];
    if (aw < W * 0.15) return null;

    const yB = Math.round(ys + len * 0.8);
    const b = fit(yB);

    const inset = W * 0.012;
    const out = {
      A: [(a[0] + inset) / W, (a[1] - inset) / W, yA / H],
      B: [(b[0] + inset) / W, (b[1] - inset) / W, yB / H],
      C: null,
      D: [(xc + aw * 0.09) / W, ys / H, (yHem - 1) / H],
    };

    if (kind !== 'vest') {
      /* Right sleeve: everything right of the body. Its lowest point is the cuff. */
      const edge = Math.round(a[1] + W * 0.01);
      let yc = -1;
      for (let y = bottom; y > yA; y--) {
        let hit = false;
        for (let x = edge; x < W; x++) if (on(x, y)) { hit = true; break; }
        if (hit) { yc = y; break; }
      }
      if (yc < 0) return null;
      const yCuff = Math.max(yA, yc - Math.round(H * 0.012));
      let sx = 0, sn = 0;
      for (let x = edge; x < W; x++) if (on(x, yCuff)) { sx += x; sn++; }
      if (!sn) return null;
      out.C = [
        (xc + shoulderW * 0.2) / W, ys / H,
        (R[yShoulder] - W * 0.025) / W, yShoulder / H,
        sx / sn / W, yCuff / H,
      ];
    }

    const ok = out.A[2] < out.B[2] && out.D[1] < out.A[2] && out.D[2] > out.B[2];
    return ok ? { lines: out, bg: `rgb(${Math.round(br)}, ${Math.round(bgc)}, ${Math.round(bb)})` } : null;
  };

  /* A studio packshot has the same plain, light backdrop in all four corners; a lifestyle photo does not */
  const studio = (img) => {
    const W = 60;
    const H = Math.round((img.naturalHeight / img.naturalWidth) * W) || 90;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    const px = ctx.getImageData(0, 0, W, H).data;
    const patches = [[0, 0], [W - 5, 0], [0, H - 5], [W - 5, H - 5]].map(([x0, y0]) => {
      const v = [];
      for (let y = y0; y < y0 + 5; y++) for (let x = x0; x < x0 + 5; x++) {
        const i = (y * W + x) * 4;
        v.push([px[i], px[i + 1], px[i + 2]]);
      }
      const mean = [0, 1, 2].map((k) => v.reduce((a, p) => a + p[k], 0) / v.length);
      const spread = Math.max(...v.map((p) => Math.abs(p[0] - mean[0]) + Math.abs(p[1] - mean[1]) + Math.abs(p[2] - mean[2])));
      return { mean, spread };
    });
    const m0 = patches[0].mean;
    const even = patches.every((p) => p.spread < 24 && Math.abs(p.mean[0] - m0[0]) + Math.abs(p.mean[1] - m0[1]) + Math.abs(p.mean[2] - m0[2]) < 30);
    const light = m0[0] + m0[1] + m0[2] > 3 * 150;
    return even && light;
  };

  const loadSmall = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

  /* ---------- Drawing ---------- */
  const NS = 'http://www.w3.org/2000/svg';
  const draw = (fig, lines) => {
    const ar = parseFloat(getComputedStyle(fig).getPropertyValue('--se-ar')) || 2 / 3;
    const VW = 1000, VH = 1000 / ar;
    const X = (x) => (x * VW).toFixed(1), Y = (y) => (y * VH).toFixed(1);
    const t = 9; /* end tick, half length */
    let d = '';
    const hLine = (v) => {
      if (!v) return;
      d += `M${X(v[0])} ${Y(v[2])}H${X(v[1])}M${X(v[0])} ${(v[2] * VH - t).toFixed(1)}v${t * 2}M${X(v[1])} ${(v[2] * VH - t).toFixed(1)}v${t * 2}`;
    };
    hLine(lines.A);
    hLine(lines.B);
    /* D is measured on the back of the jacket: drawn dashed, in its own path */
    let dBack = '';
    if (lines.D) {
      const [x, y1, y2] = lines.D;
      dBack = `M${X(x)} ${Y(y1)}V${Y(y2)}M${(x * VW - t).toFixed(1)} ${Y(y1)}h${t * 2}M${(x * VW - t).toFixed(1)} ${Y(y2)}h${t * 2}`;
    }
    if (lines.C) {
      const c = lines.C;
      const P = [[c[0] * VW, c[1] * VH], [c[2] * VW, c[3] * VH], [c[4] * VW, c[5] * VH]];
      d += `M${P[0][0].toFixed(1)} ${P[0][1].toFixed(1)}L${P[1][0].toFixed(1)} ${P[1][1].toFixed(1)}L${P[2][0].toFixed(1)} ${P[2][1].toFixed(1)}`;
      const tick = (p, q) => {
        const dx = q[0] - p[0], dy = q[1] - p[1], l = Math.hypot(dx, dy) || 1;
        const nx = (-dy / l) * t, ny = (dx / l) * t;
        d += `M${(p[0] - nx).toFixed(1)} ${(p[1] - ny).toFixed(1)}L${(p[0] + nx).toFixed(1)} ${(p[1] + ny).toFixed(1)}`;
      };
      tick(P[0], P[1]);
      tick(P[2], P[1]);
    }

    let svg = fig.querySelector('.se-pd__photo-lines');
    if (!svg) {
      svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'se-pd__photo-lines');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.appendChild(document.createElementNS(NS, 'path'));
      fig.appendChild(svg);
    }
    if (svg.children.length < 2) {
      const back = document.createElementNS(NS, 'path');
      back.setAttribute('class', 'is-back');
      svg.appendChild(back);
    }
    svg.setAttribute('viewBox', `0 0 ${VW} ${VH.toFixed(1)}`);
    svg.children[0].setAttribute('d', d);
    svg.children[1].setAttribute('d', dBack);

    /* Letters: A and B a quarter in from the left, C halfway down the sleeve, D between A and B */
    const pins = {
      A: lines.A && [lines.A[0] + (lines.A[1] - lines.A[0]) * 0.22, lines.A[2]],
      B: lines.B && [lines.B[0] + (lines.B[1] - lines.B[0]) * 0.22, lines.B[2]],
      C: lines.C && [lines.C[2] + (lines.C[4] - lines.C[2]) * 0.45, lines.C[3] + (lines.C[5] - lines.C[3]) * 0.45],
      D: lines.D && [lines.D[0], lines.A && lines.B ? (lines.A[2] + lines.B[2]) / 2 : (lines.D[1] + lines.D[2]) / 2],
    };
    Object.keys(pins).forEach((k) => {
      let el = fig.querySelector(`.se-pd__photo-pin[data-k="${k}"]`);
      if (!pins[k]) { if (el) el.remove(); return; }
      if (!el) {
        el = document.createElement('span');
        el.className = 'se-pd__photo-pin';
        el.dataset.k = k;
        el.setAttribute('aria-hidden', 'true');
        el.textContent = k;
        fig.appendChild(el);
      }
      el.style.left = `${pins[k][0] * 100}%`;
      el.style.top = `${pins[k][1] * 100}%`;
    });
    fig.classList.add('is-drawn');
  };

  const setBackdrop = (fig, colour) => {
    if (!colour) return;
    const box = fig.closest('.se-pd__fig-teaser, .se-pd__panel-fig, .se-me__card');
    if (box) box.style.setProperty('--se-photo-bg', colour);
  };

  const fallback = (fig) => {
    const tpl = fig.querySelector('template[data-se-photo-fallback]');
    const teaser = fig.closest('.se-pd__fig-teaser');
    if (teaser) teaser.classList.remove('se-pd__fig-teaser--photo');
    if (tpl) fig.replaceWith(tpl.content.cloneNode(true));
    else fig.remove();
  };

  /* ---------- Per model ---------- */
  const models = new Map(); /* model -> { figs, auto, lines, kind } */

  const linesFor = (m) => {
    if (EDIT) {
      const loc = localLines()[m.name];
      if (loc) return { lines: loc, source: 'saved here' };
    }
    if (themeLines[m.name]) return { lines: themeLines[m.name], source: 'saved in theme' };
    if (m.auto) return { lines: m.auto, source: 'automatic' };
    return null;
  };

  const render = (m) => {
    m.done = true;
    /* Not a studio packshot (e.g. a lifestyle photo first): the product page keeps the drawing */
    if (!m.packshot && !BULK) { m.figs.forEach(fallback); m.figs = []; return; }
    if (BULK) m.figs.forEach((f) => { const card = f.closest('[data-se-card]'); if (card) card.classList.toggle('is-not-packshot', !m.packshot); });
    const got = linesFor(m);
    if (!got && !EDIT) { m.figs.forEach(fallback); m.figs = []; return; }
    const lines = got ? got.lines : clone(TEMPLATE);
    if (m.kind === 'vest') lines.C = null;
    m.lines = lines;
    m.source = got ? got.source : 'template';
    m.figs.forEach((f) => draw(f, lines));
    if (EDIT) editor(m);
  };

  const start = (fig) => {
    if (fig.dataset.seStarted) return;
    fig.dataset.seStarted = '1';
    const name = fig.dataset.model;
    let m = models.get(name);
    if (m) {
      if (m.done && !m.lines) { fallback(fig); return; }
      m.figs.push(fig);
      if (m.lines) { draw(fig, m.lines); setBackdrop(fig, m.bg); if (EDIT) handles(m, fig); } return; }
    m = { name, kind: fig.dataset.kind, figs: [fig], auto: null, bg: null };
    models.set(name, m);
    loadSmall(fig.dataset.src).then((img) => {
      let res = null;
      m.packshot = false;
      if (img) {
        try { m.packshot = studio(img); } catch (e) { m.packshot = false; }
        try { res = detect(img, m.kind); } catch (e) { res = null; }
      }
      if (res) { m.auto = res.lines; m.bg = res.bg; }
      m.figs.forEach((f) => setBackdrop(f, m.bg));
      render(m);
    });
  };

  /* ---------- Adjust mode ---------- */
  let panel = null;
  const saveLocal = (m) => {
    const all = localLines();
    all[m.name] = tidy(m.lines);
    store.set(LOCAL_KEY, JSON.stringify(all));
    m.source = 'saved here';
    updatePanel(m);
  };

  const POINTS = {
    A1: { get: (L) => [L.A[0], L.A[2]], set: (L, x, y) => { L.A[0] = x; L.A[2] = y; } },
    A2: { get: (L) => [L.A[1], L.A[2]], set: (L, x, y) => { L.A[1] = x; L.A[2] = y; } },
    B1: { get: (L) => [L.B[0], L.B[2]], set: (L, x, y) => { L.B[0] = x; L.B[2] = y; } },
    B2: { get: (L) => [L.B[1], L.B[2]], set: (L, x, y) => { L.B[1] = x; L.B[2] = y; } },
    C1: { get: (L) => [L.C[0], L.C[1]], set: (L, x, y) => { L.C[0] = x; L.C[1] = y; }, c: true },
    C2: { get: (L) => [L.C[2], L.C[3]], set: (L, x, y) => { L.C[2] = x; L.C[3] = y; }, c: true },
    C3: { get: (L) => [L.C[4], L.C[5]], set: (L, x, y) => { L.C[4] = x; L.C[5] = y; }, c: true },
    D1: { get: (L) => [L.D[0], L.D[1]], set: (L, x, y) => { L.D[0] = x; L.D[1] = y; } },
    D2: { get: (L) => [L.D[0], L.D[2]], set: (L, x, y) => { L.D[0] = x; L.D[2] = y; } },
  };

  const placeHandles = (m) => {
    m.figs.forEach((fig) => fig.querySelectorAll('.se-pd__photo-handle').forEach((h) => {
      const p = POINTS[h.dataset.p];
      if (p.c && !m.lines.C) { h.hidden = true; return; }
      h.hidden = false;
      const [x, y] = p.get(m.lines);
      h.style.left = `${x * 100}%`;
      h.style.top = `${y * 100}%`;
    }));
  };

  const handles = (m, fig) => {
    fig.classList.add('is-editing');
    if (!fig.querySelector('.se-pd__photo-handle')) {
      Object.keys(POINTS).forEach((k) => {
        const h = document.createElement('span');
        h.className = 'se-pd__photo-handle';
        h.dataset.p = k;
        h.title = k;
        fig.appendChild(h);
        h.addEventListener('pointerdown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          h.setPointerCapture(e.pointerId);
          const move = (ev) => {
            const box = fig.getBoundingClientRect();
            const x = Math.min(1, Math.max(0, (ev.clientX - box.left) / box.width));
            const y = Math.min(1, Math.max(0, (ev.clientY - box.top) / box.height));
            POINTS[k].set(m.lines, x, y);
            m.figs.forEach((f) => draw(f, m.lines));
            placeHandles(m);
          };
          const up = () => {
            h.removeEventListener('pointermove', move);
            h.removeEventListener('pointerup', up);
            h.removeEventListener('pointercancel', up);
            saveLocal(m);
          };
          h.addEventListener('pointermove', move);
          h.addEventListener('pointerup', up);
          h.addEventListener('pointercancel', up);
        });
      });
    }
    placeHandles(m);
  };

  const copy = (text, btn) => {
    const done = () => { const t = btn.textContent; btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = t; }, 1400); };
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, () => window.prompt('Copy:', text));
    else window.prompt('Copy:', text);
  };

  const updatePanel = (m) => {
    if (BULK) {
      m.figs.forEach((f) => {
        const card = f.closest('[data-se-card]');
        if (!card) return;
        card.dataset.source = m.source;
        const el = card.querySelector('[data-source]');
        if (el) el.textContent = m.source;
      });
      document.querySelectorAll('[data-se-measure-count]').forEach((el) => { el.textContent = Object.keys(localLines()).length; });
      return;
    }
    if (!panel) return;
    panel.querySelector('[data-name]').textContent = m.name;
    panel.querySelector('[data-source]').textContent = m.source;
    panel.querySelector('[data-count]').textContent = Object.keys(localLines()).length;
  };

  const editor = (m) => {
    m.figs.forEach((f) => handles(m, f));
    if (!panel && !BULK) {
      panel = document.createElement('div');
      panel.className = 'se-measure-edit';
      panel.innerHTML =
        '<p><strong data-name></strong> · <span data-source></span></p>' +
        '<p class="se-measure-edit__hint">Drag the dots. Edits are kept in this browser.</p>' +
        '<div class="se-measure-edit__row">' +
        '<button type="button" data-act="all">Copy all (<span data-count>0</span>)</button>' +
        '<button type="button" data-act="one">Copy this model</button>' +
        '<button type="button" data-act="auto">Back to automatic</button>' +
        '<button type="button" data-act="exit">Exit</button>' +
        '</div>';
      document.body.appendChild(panel);
      panel.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b) return;
        const act = b.dataset.act;
        if (act === 'all') copy(JSON.stringify({ ...themeLines, ...localLines() }, null, 1), b);
        if (act === 'one') copy(`"${m.name}": ${JSON.stringify(tidy(m.lines))}`, b);
        if (act === 'auto') {
          const all = localLines();
          delete all[m.name];
          store.set(LOCAL_KEY, JSON.stringify(all));
          render(m);
        }
        if (act === 'exit') { store.del('se-measure-edit'); location.href = location.pathname; }
      });
    }
    updatePanel(m);
  };

  /* For the bulk editor page */
  const allLines = () => JSON.stringify({ ...themeLines, ...localLines() }, null, 1);
  window.SEMeasure = {
    approve(name) { const m = models.get(name); if (m && m.lines) saveLocal(m); },
    reset(name) {
      const m = models.get(name);
      if (!m) return;
      const all = localLines();
      delete all[name];
      store.set(LOCAL_KEY, JSON.stringify(all));
      render(m);
    },
    copyAll(btn) { copy(allLines(), btn); },
    clearAll() { store.del(LOCAL_KEY); models.forEach((m) => render(m)); },
  };

  if (EDIT) {
    /* The desktop figure is a button that opens the measurements; while adjusting, clicks stay on the figure */
    document.addEventListener('click', (e) => {
      if (e.target.closest('.se-pd__photo.is-editing')) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  }

  /* Work only starts when the figure comes near the screen, so the page load is untouched */
  const io = (!EDIT || BULK) && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      start(e.target);
    }), { rootMargin: '800px 0px' })
    : null;
  const find = () => document.querySelectorAll('[data-se-photo]').forEach((f) => (io ? io.observe(f) : start(f)));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', find, { once: true });
  else find();
})();
